// Decimal strings from the print API, kept exact: amounts can have 18 digits and quantities 8
// decimals, more than a JavaScript number holds. Sums are done on the digit strings themselves
// (no BigInt, which older phones opening a shared invoice link do not have).

// The page font draws Latin digits as Persian ones; the separators are the Persian ones, because a
// Latin «.» beside a Persian digit reads as the Persian zero «۰».
export const THOUSANDS_SEPARATOR = '٬' // ٬
export const DECIMAL_SEPARATOR = '٫' // ٫

const NUMBER = /^-?\d+(\.\d+)?$/

export const isNumberText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && NUMBER.test(value.trim())

/** «1٬234٬567٫25» for "1234567.2500"; anything that is not a plain number is returned as it is. */
export const formatNumber = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return ''
  const text = value.trim()
  if (!NUMBER.test(text)) return text

  const negative = text.startsWith('-')
  const [integerPart, fraction = ''] = text.replace(/^-/, '').split('.')
  const integer = integerPart.replace(/^0+(?=\d)/, '')
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, THOUSANDS_SEPARATOR)
  const decimals = fraction.replace(/0+$/, '')
  const sign = negative && (integer !== '0' || decimals !== '') ? '-' : ''
  return sign + grouped + (decimals ? DECIMAL_SEPARATOR + decimals : '')
}

type Signed = {negative: boolean; digits: string}

/** The value as a sign and its digits at the given number of decimals ("12.5", 2 → 1250). */
const toDigits = (value: string, scale: number): Signed => {
  const text = value.trim()
  const negative = text.startsWith('-')
  const [integerPart, fraction = ''] = text.replace(/^-/, '').split('.')
  const digits = (integerPart + fraction.padEnd(scale, '0')).replace(/^0+(?=\d)/, '')
  return {negative: negative && /[1-9]/.test(digits), digits}
}

const compareDigits = (a: string, b: string): number =>
  a.length !== b.length ? a.length - b.length : a < b ? -1 : a > b ? 1 : 0

const addDigits = (a: string, b: string): string => {
  let result = ''
  let carry = 0
  for (let i = a.length - 1, j = b.length - 1; i >= 0 || j >= 0 || carry; i--, j--) {
    const sum = (i >= 0 ? +a[i] : 0) + (j >= 0 ? +b[j] : 0) + carry
    result = String(sum % 10) + result
    carry = sum >= 10 ? 1 : 0
  }
  return result.replace(/^0+(?=\d)/, '')
}

/** a − b for a ≥ b. */
const subtractDigits = (a: string, b: string): string => {
  let result = ''
  let borrow = 0
  for (let i = a.length - 1, j = b.length - 1; i >= 0; i--, j--) {
    let difference = +a[i] - (j >= 0 ? +b[j] : 0) - borrow
    borrow = difference < 0 ? 1 : 0
    if (difference < 0) difference += 10
    result = String(difference) + result
  }
  return result.replace(/^0+(?=\d)/, '')
}

const addSigned = (a: Signed, b: Signed): Signed => {
  if (a.negative === b.negative)
    return {negative: a.negative, digits: addDigits(a.digits, b.digits)}
  const order = compareDigits(a.digits, b.digits)
  if (order === 0) return {negative: false, digits: '0'}
  return order > 0
    ? {negative: a.negative, digits: subtractDigits(a.digits, b.digits)}
    : {negative: b.negative, digits: subtractDigits(b.digits, a.digits)}
}

const fromDigits = (value: Signed, scale: number): string => {
  const padded = value.digits.padStart(scale + 1, '0')
  const integer = scale > 0 ? padded.slice(0, padded.length - scale) : padded
  const fraction = scale > 0 ? padded.slice(padded.length - scale).replace(/0+$/, '') : ''
  const zero = /^0+$/.test(integer) && fraction === ''
  return (value.negative && !zero ? '-' : '') + integer + (fraction ? '.' + fraction : '')
}

/** The exact sum of the numeric values; null when none of them is a number. */
export const sumNumbers = (values: (string | null | undefined)[]): string | null => {
  const numbers = values.filter(isNumberText)
  if (numbers.length === 0) return null
  const scale = Math.max(...numbers.map((x) => (x.trim().split('.')[1] || '').length))
  const total = numbers
    .map((x) => toDigits(x, scale))
    .reduce((sum, value) => addSigned(sum, value), {negative: false, digits: '0'} as Signed)
  return fromDigits(total, scale)
}

export const isZeroNumber = (value: string | null | undefined): boolean =>
  isNumberText(value) && /^-?0+(\.0+)?$/.test(value.trim())

/** Present and not zero. */
export const hasAmount = (value: string | null | undefined): boolean =>
  isNumberText(value) && !isZeroNumber(value)

/** «10٪» for "10", «9٫5٪» for "9.5". */
export const formatPercent = (value: string | null | undefined): string =>
  isNumberText(value) ? `${formatNumber(value)}٪` : ''
